import { PaletteMode, Components, Theme } from '@mui/material';
import { CSSProperties } from 'react';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import {
  STATIC_CHIP_TYPES,
  STATIC_CHIP_VARIANTS,
  INTERACTIVE_CHIP_VARIANTS,
  INTERACTIVE_CHIP_STATES,
} from '../../components/Chip/constants';

// Define custom interfaces for Chip-specific props
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    // CHIPSTATIC VARIANTS — 3 types × 9 variants = 27 entries.
    // Generated from `STATIC_CHIP_TYPES × STATIC_CHIP_VARIANTS`; keep in sync.
    'static-filled-primary': true;
    'static-filled-accent': true;
    'static-filled-neutral': true;
    'static-filled-success': true;
    'static-filled-high-alert': true;
    'static-filled-medium-alert': true;
    'static-filled-error': true;
    'static-filled-light': true;
    'static-filled-subtle-attention': true;
    'static-outline-primary': true;
    'static-outline-accent': true;
    'static-outline-neutral': true;
    'static-outline-success': true;
    'static-outline-high-alert': true;
    'static-outline-medium-alert': true;
    'static-outline-error': true;
    'static-outline-light': true;
    'static-outline-subtle-attention': true;
    'static-transparent-primary': true;
    'static-transparent-accent': true;
    'static-transparent-neutral': true;
    'static-transparent-success': true;
    'static-transparent-high-alert': true;
    'static-transparent-medium-alert': true;
    'static-transparent-error': true;
    'static-transparent-light': true;
    'static-transparent-subtle-attention': true;

    // CHIPINTERACTIVE VARIANTS — 3 variants × 3 states = 9 entries.
    // Generated from `INTERACTIVE_CHIP_VARIANTS × INTERACTIVE_CHIP_STATES`.
    // Note: `hover` is also applied to `:focus-visible` at the wrapper level.
    'interactive-primary-resting': true;
    'interactive-primary-hover': true;
    'interactive-primary-selected': true;
    'interactive-neutral-resting': true;
    'interactive-neutral-hover': true;
    'interactive-neutral-selected': true;
    'interactive-error-resting': true;
    'interactive-error-hover': true;
    'interactive-error-selected': true;

    // BADGE VARIANTS
    // Primary variants
    'primary-solid': true;
    'primary-outline': true;
    'primary-subtle': true;
    // Accent variants
    'accent-solid': true;
    'accent-outline': true;
    'accent-subtle': true;
    // Neutral variants
    'neutral-solid': true;
    'neutral-outline': true;
    'neutral-subtle': true;
    // Success variants
    'success-solid': true;
    'success-outline': true;
    'success-subtle': true;
    // Warning variants
    'warning-solid': true;
    'warning-outline': true;
    'warning-subtle': true;
    // Error variants
    'error-solid': true;
    'error-outline': true;
    'error-subtle': true;
    // Medium variants
    'medium-solid': true;
    'medium-outline': true;
    'medium-subtle': true;
    // Info variants
    'info-solid': true;
    'info-outline': true;
    'info-subtle': true;

    // TAG VARIANTS
    neutral: true;
    neutral2: true;
    brand: true;
    accent: true;
    success: true;
    warning: true;
    error: true;
    info: true;
    condition: true;
  }

  interface ChipPropsSizeOverrides {
    small: true;
    medium: true;
    large: true;
    'tag-default': true;
    // ChipStatic / ChipInteractive sizes — kebab-case, mirrors Figma `Size`.
    lg: true;
    md: true;
    sm: true;
  }
}

export const MuiChipTheme = (mode: PaletteMode): Components<Theme>['MuiChip'] => {
  const { tokens, componentTokens } = themePalette(mode);

  /**
   * `ChipStatic` variant blocks — generated from the const tuples so the 27
   * (type × variant) permutations stay in lockstep with `Chip/constants.ts`
   * and `componentTokens.chip.static`.
   *
   * Adding a new type or variant requires only:
   *   1. Extend the tuple in `components/Chip/constants.ts`.
   *   2. Add the matching row to `componentTokens.chip.static` in `component-tokens.ts`.
   *
   * No edits to this block are needed — the cross-product picks them up.
   */
  const staticChipVariantBlocks = STATIC_CHIP_TYPES.flatMap(type =>
    STATIC_CHIP_VARIANTS.map(variant => {
      const slot = componentTokens.chip.static[type][variant];
      return {
        props: { variant: `static-${type}-${variant}` as const },
        style: {
          backgroundColor: slot.background.standard,
          borderColor: slot.stroke.default,
          color: slot.text.default,
          '& .MuiChip-icon': {
            color: slot.asset.default,
          },
        },
      };
    }),
  );

  /**
   * `ChipInteractive` variant blocks — generated from the const tuples so
   * the 9 (variant × state) permutations stay in lockstep with
   * `Chip/constants.ts` and `componentTokens.chip.interactive`.
   *
   * `hover` is _additionally_ applied to `:focus-visible` at the wrapper
   * level so keyboard-only users get the same elevated visual treatment
   * as mouse hover. The resting-state row sets the `:hover, :focus-visible`
   * override to point at the hover token bucket.
   */
  const interactiveChipVariantBlocks = INTERACTIVE_CHIP_VARIANTS.flatMap(variant =>
    INTERACTIVE_CHIP_STATES.map(state => {
      const slot = componentTokens.chip.interactive[variant][state];
      const hoverSlot = componentTokens.chip.interactive[variant].hover;
      return {
        props: { variant: `interactive-${variant}-${state}` as const },
        style: {
          backgroundColor: slot.background,
          borderColor: slot.stroke,
          color: slot.label,
          // CSS custom property consumed by <ChipInteractive.Field> — keeps
          // the prefix-label cyan resolution next to the rest of the
          // (variant × state) row, instead of forcing the Field subcomponent
          // to subscribe to ThemeContext separately.
          '--chip-interactive-field-color': slot.fieldLabel,
          '& .MuiChip-icon': {
            color: slot.asset,
          },
          '& .MuiChip-deleteIcon': {
            color: slot.asset,
            '&:hover, &:focus-visible': { color: slot.asset, opacity: 0.8 },
          },
          // Both `resting` and `selected` rows promote to the `hover` token
          // bucket on mouse hover / keyboard focus. Per design, a selected
          // chip on hover should look identical to an unselected chip on
          // hover — not "selected + extra darken" — so the same override
          // applies to both rows. The `hover` row itself doesn't need its
          // own override (its base style already is the hover treatment).
          ...(state !== 'hover'
            ? {
                '&:hover, &:focus-visible': {
                  backgroundColor: hoverSlot.background,
                  borderColor: hoverSlot.stroke,
                },
              }
            : {}),
        },
      };
    }),
  );

  const commonBadgeStyles: CSSProperties = {
    border: '1px solid',
    borderRadius: '4px',
    padding: '0',
    height: 'auto',
    borderColor: tokens.stroke.basic.clear,
    backgroundColor: 'transparent',
    color: tokens.content.text.neutral.base,
    width: 'fit-content !important',
    fontWeight: typographyTokens.fontWeights.medium,
  };

  return {
    styleOverrides: {
      root: {
        ...commonBadgeStyles,
        // Override MUI defaults to match our design system
        '&.Mui-disabled': {
          opacity: 0.4,
          pointerEvents: 'auto',
          cursor: 'not-allowed',
        },
      },
      label: {
        // Remove default padding to have full control
        padding: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      },
      icon: {
        width: '12px',
        height: '12px',
        margin: 0,
        marginRight: 0.5,
        '&.MuiChip-iconLarge': {
          width: '16px',
          height: '16px',
          marginRight: 1,
        },
      },
    },
    variants: [
      // ChipStatic variant blocks — generated cross-product, kept in lockstep
      // with `Chip/constants.ts` and `componentTokens.chip.static`.
      ...staticChipVariantBlocks,

      // ChipInteractive variant blocks — generated cross-product, kept in
      // lockstep with `Chip/constants.ts` and `componentTokens.chip.interactive`.
      ...interactiveChipVariantBlocks,

      // Clickable variant - re-enable pointer events when clickable
      {
        props: { clickable: true },
        style: {
          pointerEvents: 'auto',
          cursor: 'pointer',
        },
      },

      // Size variants
      {
        props: { size: 'small' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
          padding: '3px 6px',
        },
      },
      {
        props: { size: 'medium' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
          padding: '5px 6px',
        },
      },
      {
        props: { size: 'large' },
        style: {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
          padding: '5px 8px',
        },
      },
      {
        props: { size: 'tag-default' },
        style: {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xxs,
          fontWeight: typographyTokens.fontWeights.semibold,
          padding: '8px',
        },
      },

      // Primary variants
      {
        props: { variant: 'primary-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.brand.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.brand.background.standard,
          color: componentTokens.badges.colors.solid.brand.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.brand.assets.default,
          },
        },
      },
      {
        props: { variant: 'primary-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.brand.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.brand.background.standard,
          color: componentTokens.badges.colors.outline.brand.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.brand.assets.default,
          },
        },
      },
      {
        props: { variant: 'primary-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.brand.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.brand.background.standard,
          color: componentTokens.badges.colors.subtle.brand.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.brand.assets.default,
          },
        },
      },

      // Accent variants
      {
        props: { variant: 'accent-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.accent.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.accent.background.standard,
          color: componentTokens.badges.colors.solid.accent.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.accent.assets.default,
          },
        },
      },
      {
        props: { variant: 'accent-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.accent.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.accent.background.standard,
          color: componentTokens.badges.colors.outline.accent.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.accent.assets.default,
          },
        },
      },
      {
        props: { variant: 'accent-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.accent.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.accent.background.standard,
          color: componentTokens.badges.colors.subtle.accent.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.accent.assets.default,
          },
        },
      },

      // Neutral variants
      {
        props: { variant: 'neutral-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.neutral.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.neutral.background.standard,
          color: componentTokens.badges.colors.solid.neutral.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.neutral.assets.default,
          },
        },
      },
      {
        props: { variant: 'neutral-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.neutral.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.neutral.background.standard,
          color: componentTokens.badges.colors.outline.neutral.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.neutral.assets.default,
          },
        },
      },
      {
        props: { variant: 'neutral-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.neutral.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.neutral.background.standard,
          color: componentTokens.badges.colors.subtle.neutral.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.neutral.assets.default,
          },
        },
      },

      // Success variants
      {
        props: { variant: 'success-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.success.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.success.background.standard,
          color: componentTokens.badges.colors.solid.success.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.success.assets.default,
          },
        },
      },
      {
        props: { variant: 'success-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.success.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.success.background.standard,
          color: componentTokens.badges.colors.outline.success.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.success.assets.default,
          },
        },
      },
      {
        props: { variant: 'success-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.success.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.success.background.standard,
          color: componentTokens.badges.colors.subtle.success.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.success.assets.default,
          },
        },
      },

      // Warning variants
      {
        props: { variant: 'warning-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.warning.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.warning.background.standard,
          color: componentTokens.badges.colors.solid.warning.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.warning.assets.default,
          },
        },
      },
      {
        props: { variant: 'warning-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.warning.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.warning.background.standard,
          color: componentTokens.badges.colors.outline.warning.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.warning.assets.default,
          },
        },
      },
      {
        props: { variant: 'warning-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.warning.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.warning.background.standard,
          color: componentTokens.badges.colors.subtle.warning.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.warning.assets.default,
          },
        },
      },

      // Error variants
      {
        props: { variant: 'error-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.error.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.error.background.standard,
          color: componentTokens.badges.colors.solid.error.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.error.assets.default,
          },
        },
      },
      {
        props: { variant: 'error-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.error.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.error.background.standard,
          color: componentTokens.badges.colors.outline.error.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.error.assets.default,
          },
        },
      },
      {
        props: { variant: 'error-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.error.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.error.background.standard,
          color: componentTokens.badges.colors.subtle.error.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.error.assets.default,
          },
        },
      },

      // Medium variants
      {
        props: { variant: 'medium-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.medium.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.medium.background.standard,
          color: componentTokens.badges.colors.solid.medium.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.medium.assets.default,
          },
        },
      },
      {
        props: { variant: 'medium-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.medium.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.medium.background.standard,
          color: componentTokens.badges.colors.outline.medium.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.medium.assets.default,
          },
        },
      },
      {
        props: { variant: 'medium-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.medium.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.medium.background.standard,
          color: componentTokens.badges.colors.subtle.medium.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.medium.assets.default,
          },
        },
      },

      // Info variants
      {
        props: { variant: 'info-solid' },
        style: {
          borderColor: componentTokens.badges.colors.solid.info.stroke.default,
          backgroundColor: componentTokens.badges.colors.solid.info.background.standard,
          color: componentTokens.badges.colors.solid.info.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.solid.info.assets.default,
          },
        },
      },
      {
        props: { variant: 'info-outline' },
        style: {
          borderColor: componentTokens.badges.colors.outline.info.stroke.default,
          backgroundColor: componentTokens.badges.colors.outline.info.background.standard,
          color: componentTokens.badges.colors.outline.info.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.outline.info.assets.default,
          },
        },
      },
      {
        props: { variant: 'info-subtle' },
        style: {
          borderColor: componentTokens.badges.colors.subtle.info.stroke.default,
          backgroundColor: componentTokens.badges.colors.subtle.info.background.standard,
          color: componentTokens.badges.colors.subtle.info.text.default,
          '& .MuiChip-icon': {
            color: componentTokens.badges.colors.subtle.info.assets.default,
          },
        },
      },

      // Tag variants
      {
        props: { variant: 'neutral' },
        style: {
          borderColor: componentTokens.tags.colors.neutral.stroke.resting,
          backgroundColor: componentTokens.tags.colors.neutral.background.resting,
          color: componentTokens.tags.colors.neutral.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.neutral.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.neutral.stroke.hover,
            backgroundColor: componentTokens.tags.colors.neutral.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.neutral.stroke.hover,
            backgroundColor: componentTokens.tags.colors.neutral.background.hover,
          },
        },
      },
      {
        props: { variant: 'neutral2' },
        style: {
          borderColor: componentTokens.tags.colors.neutral2.stroke.resting,
          backgroundColor: componentTokens.tags.colors.neutral2.background.resting,
          color: componentTokens.tags.colors.neutral2.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.neutral2.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.neutral2.stroke.hover,
            backgroundColor: componentTokens.tags.colors.neutral2.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.neutral.stroke.hover,
            backgroundColor: componentTokens.tags.colors.neutral.background.hover,
          },
        },
      },
      {
        props: { variant: 'brand' },
        style: {
          borderColor: componentTokens.tags.colors.brand.stroke.resting,
          backgroundColor: componentTokens.tags.colors.brand.background.resting,
          color: componentTokens.tags.colors.brand.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.brand.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.brand.stroke.hover,
            backgroundColor: componentTokens.tags.colors.brand.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.brand.stroke.hover,
            backgroundColor: componentTokens.tags.colors.brand.background.hover,
          },
        },
      },
      {
        props: { variant: 'accent' },
        style: {
          borderColor: componentTokens.tags.colors.accent.stroke.resting,
          backgroundColor: componentTokens.tags.colors.accent.background.resting,
          color: componentTokens.tags.colors.accent.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.accent.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.accent.stroke.hover,
            backgroundColor: componentTokens.tags.colors.accent.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.accent.stroke.hover,
            backgroundColor: componentTokens.tags.colors.accent.background.hover,
          },
        },
      },
      {
        props: { variant: 'success' },
        style: {
          borderColor: componentTokens.tags.colors.success.stroke.resting,
          backgroundColor: componentTokens.tags.colors.success.background.resting,
          color: componentTokens.tags.colors.success.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.success.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.success.stroke.hover,
            backgroundColor: componentTokens.tags.colors.success.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.success.stroke.hover,
            backgroundColor: componentTokens.tags.colors.success.background.hover,
          },
        },
      },
      {
        props: { variant: 'warning' },
        style: {
          borderColor: componentTokens.tags.colors.warning.stroke.resting,
          backgroundColor: componentTokens.tags.colors.warning.background.resting,
          color: componentTokens.tags.colors.warning.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.warning.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.warning.stroke.hover,
            backgroundColor: componentTokens.tags.colors.warning.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.warning.stroke.hover,
            backgroundColor: componentTokens.tags.colors.warning.background.hover,
          },
        },
      },
      {
        props: { variant: 'error' },
        style: {
          borderColor: componentTokens.tags.colors.error.stroke.resting,
          backgroundColor: componentTokens.tags.colors.error.background.resting,
          color: componentTokens.tags.colors.error.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.error.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.error.stroke.hover,
            backgroundColor: componentTokens.tags.colors.error.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.error.stroke.hover,
            backgroundColor: componentTokens.tags.colors.error.background.hover,
          },
        },
      },
      {
        props: { variant: 'info' },
        style: {
          borderColor: componentTokens.tags.colors.info.stroke.resting,
          backgroundColor: componentTokens.tags.colors.info.background.resting,
          color: componentTokens.tags.colors.info.text.labelBase,
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.info.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.info.stroke.hover,
            backgroundColor: componentTokens.tags.colors.info.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.info.stroke.hover,
            backgroundColor: componentTokens.tags.colors.info.background.hover,
          },
        },
      },
      {
        props: { variant: 'condition' },
        style: {
          borderColor: componentTokens.tags.colors.condition.stroke.resting,
          backgroundColor: componentTokens.tags.colors.condition.background.resting,
          color: componentTokens.tags.colors.condition.text.labelBase,
          '& .MuiChip-label': {
            gap: 2,
          },
          '& .MuiChip-icon': {
            color: componentTokens.tags.colors.condition.assets.leadIconBase,
          },
          '&:hover:not(.Mui-disabled)': {
            cursor: 'pointer',
            borderColor: componentTokens.tags.colors.condition.stroke.hover,
            backgroundColor: componentTokens.tags.colors.condition.background.hover,
          },
          '&:focus': {
            borderColor: componentTokens.tags.colors.condition.stroke.hover,
            backgroundColor: componentTokens.tags.colors.condition.background.hover,
          },
        },
      },
    ],
  };
};
